import { Info } from 'lucide-react'
import { type FieldError, type FieldValues, type Path, type PathValue, type UseFormRegister, type UseFormSetValue } from 'react-hook-form'

import { Tooltip } from 'slab'

import { ReimbursementPurpose } from './types'
import { FormDateFieldDS } from '../reusable/form/FormFields'

export interface TransactionDateInputProps<T extends FieldValues> {
  error?: FieldError;
  minDate?: Date;
  transactionDate: string;
  purpose: string;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
}

export const TransactionDateInput = <T extends FieldValues>({
  error,
  minDate,
  purpose,
  transactionDate,
  register,
  setValue
}: TransactionDateInputProps<T>) => {
  const label = purpose === ReimbursementPurpose.PREMIUM ? 'Coverage Date' : 'Service Date'
  const tooltipText = purpose === ReimbursementPurpose.PREMIUM ? 'The month covered by your premium payment' : 'The date the service took place'
  const fieldId = 'transaction_date' as Path<T>

  const handleTwoDigitYearOnBlur = () => {
    let value = transactionDate
    // Transform two digit year to current century if the first two digits are '00' (inputs of type 'date' give back
    // values in YYYY-MM-DD format like '0025-02-28')
    if (value.slice(0, 2) === '00') {
      const century = Math.trunc(new Date().getFullYear() / 100).toString()
      value = century + value.slice(2)
    }

    setValue(fieldId, value as PathValue<T, Path<T>>, { shouldValidate: true, shouldTouch: true })
  }

  return (
    <FormDateFieldDS<T>
      error={error}
      fieldId={fieldId}
      onBlur={handleTwoDigitYearOnBlur}
      fieldLabel={
        <span className='space-x-2 flex'>
          <span>{label}</span>
          <Tooltip content={tooltipText}>
            <Info size={16} />
          </Tooltip>
        </span>
      }
      register={register}
      validationRule={{
        required: `${label} is required`,
        validate: (value: string) => {
          const txnDate = new Date(value)
          if (txnDate > new Date()) {
            return 'Reimbursements cannot be submitted for future transactions. For premiums, you must wait until the month of coverage to begin before submitting a reimbursement.'
          }

          if (minDate && txnDate < minDate) {
            const currentYear = new Date().getFullYear()
            return `Reimbursements for the previous year must be submitted within the first ${currentYear <= 2025 ? 60 : 90} days of the new year.`
          }

          return true
        }
      }}
    />
  )
}
